import { Link } from 'react-router-dom';
import { Badge, Box, Button, Container, Group, Stack, Text, Title } from '@mantine/core';
import { IconChartLine, IconDownload, IconLibrary, IconShieldCheckFilled } from '@tabler/icons-react';
import { motion, useReducedMotion } from 'framer-motion';

import classes from './Hero.module.css';

export function Hero() {
  const reduce = useReducedMotion();

  return (
    <Box component="section" className={classes.hero} aria-labelledby="hero-title">
      <div className={classes.glowA} aria-hidden="true" />
      <div className={classes.glowB} aria-hidden="true" />
      <div className={classes.grid} aria-hidden="true" />

      <Container size="lg" pos="relative" pt={{ base: 64, md: 96 }} pb={{ base: 64, md: 96 }}>
        <Stack align="center" gap="xl" ta="center">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.0, 0.0, 0.2, 1] }}
          >
            <Badge
              variant="outline"
              color="forge"
              size="lg"
              radius="sm"
              leftSection={<IconShieldCheckFilled size={14} />}
              styles={{
                root: {
                  borderColor: 'rgba(255, 107, 44, 0.4)',
                  background: 'rgba(255, 107, 44, 0.06)',
                  color: 'var(--tf-forge-end)',
                  letterSpacing: '0.04em',
                },
              }}
            >
              Deterministic safety · AI-powered · Now in v1.0
            </Badge>
          </motion.div>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.0, 0.0, 0.2, 1] }}
          >
            <Title id="hero-title" order={1} className={classes.title}>
              ECU tuning that knows{' '}
              <span className="tf-gradient-text">when to stop you.</span>
            </Title>
          </motion.div>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25, ease: [0.0, 0.0, 0.2, 1] }}
          >
            <Text className={classes.subtitle} maw={720}>
              An AI-assisted tuning workbench with a deterministic safety core, a community tune
              library covering 13 platforms, and an experience-level mode for first-time tuners.{' '}
              <Text component="span" fw={600} c="var(--tf-text-primary)">
                The rules engine never asks the LLM what to do.
              </Text>
            </Text>
          </motion.div>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4, ease: [0.0, 0.0, 0.2, 1] }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Group gap="md" justify="center" wrap="wrap">
              <Button
                component={Link}
                to="/download"
                size="lg"
                radius="md"
                variant="gradient"
                gradient={{ from: 'var(--tf-forge-start)', to: 'var(--tf-forge-end)', deg: 135 }}
                leftSection={<IconDownload size={20} />}
                className={classes.ctaPrimary}
              >
                Download Desktop App
              </Button>
              <Button
                component={Link}
                to="/community/tunes"
                size="lg"
                radius="md"
                variant="default"
                leftSection={<IconLibrary size={20} />}
                className={classes.ctaSecondary}
              >
                Browse Tunes
              </Button>
              <Button
                component={Link}
                to="/analyzer"
                size="lg"
                radius="md"
                variant="subtle"
                color="gray"
                leftSection={<IconChartLine size={20} />}
              >
                Try the Analyzer
              </Button>
            </Group>
          </motion.div>

          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Text size="xs" c="var(--tf-text-muted)" tt="uppercase" lts="0.12em" mt="md">
              Windows · macOS · No credit card · Open source core
            </Text>
          </motion.div>
        </Stack>
      </Container>
    </Box>
  );
}
